import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Form,
  FormGroup,
  FormFeedback,
  Alert,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { AuthButton, AuthInput } from "../../auth/style";
import StoreWhatsappOtpHint from "../components/StoreWhatsappOtpHint";
import { getCurrentUser, setAuthenticatedUser } from "../../auth/service";
import storeApi from "../storeApi";
import { trackStoreLeadSignupStart } from "../leadActivity";
import { AppContext } from "../../context";
import { trackZbRegistrationComplete } from "../../common/analytics";

const STORE_TEST_MOBILE_PATTERN = /^test_\d{10}$/;

const normalizeLeadMobile = (raw) => {
  const trimmed = String(raw ?? "").trim();
  if (STORE_TEST_MOBILE_PATTERN.test(trimmed)) return trimmed;

  const digits = trimmed.replace(/\D/g, "");
  if (digits.length === 10) return digits;
  if (digits.startsWith("91") && digits.length === 12) return digits.slice(2);
  if (digits.startsWith("0") && digits.length === 11) return digits.slice(1);
  return "";
};

const isStoreLoginMobileValid = (mobile) => {
  const value = String(mobile ?? "").trim();
  if (STORE_TEST_MOBILE_PATTERN.test(value)) return true;
  return value.length === 10 && /^\d+$/.test(value);
};

const isBuyerArchivedError = (err) => {
  const nested = err?.response?.data?.error;
  if (nested && typeof nested === "object" && nested.code === "BUYER_ARCHIVED") {
    return true;
  }
  if (err?.status === 403) {
    const msg = String(err?.error ?? "").toLowerCase();
    return msg.includes("not supported on zerobuild");
  }
  return false;
};

/**
 * Zerobuild Store - Login with OTP
 *
 * After successful login, if the user has no display name, optionally show
 * the profile step (see AuthModal onRequireRegistration) or /register with state.
 */
const StoreLoginWithOtp = ({
  isOpen = true,
  onClose = null,
  onRequireRegistration = null,
}) => {
  const {
    setCurrentUser,
    setDealerReferral,
    popAuthModalReturnTarget,
    closeAuthModal,
  } = useContext(AppContext) || {};
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState(null);
  const [isGeneratingOtp, setIsGeneratingOtp] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [mobileContinueAttempted, setMobileContinueAttempted] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && onClose == null) {
      trackStoreLeadSignupStart();
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setMobileContinueAttempted(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || mobileNumber.length > 0) return;
    try {
      const lead = normalizeLeadMobile(localStorage.getItem("storeLeadMobile"));
      if (lead) setMobileNumber(lead);
    } catch {
      // ignore
    }
  }, [isOpen, mobileNumber.length]);

  const toErrorText = (err, fallback) => {
    const raw =
      err?.message ??
      err?.error ??
      err?.response?.data?.message ??
      err?.response?.data?.error ??
      fallback;
    if (typeof raw === "string") return raw;

    if (raw && typeof raw === "object") {
      if (typeof raw.message === "string") return raw.message;
      if (typeof raw.error === "string") return raw.error;
      try {
        return JSON.stringify(raw);
      } catch {
        return fallback;
      }
    }

    return String(raw ?? fallback);
  };

  const handleClose = () => {
    if (typeof onClose === "function") {
      onClose();
      return;
    }
    if (typeof window !== "undefined") window.history.back();
  };

  const isMobileValid = isStoreLoginMobileValid(mobileNumber);

  const canGenerateOtp = isMobileValid && !isGeneratingOtp && !otpSent;
  const canVerify = otpSent && otp.trim().length > 0 && !isVerifying;

  const handleChangeMobile = () => {
    setOtpSent(false);
    setOtp("");
    setError(null);
    setMobileContinueAttempted(false);
  };

  const handleContinue = () => {
    setMobileContinueAttempted(true);
    void handleGenerateOtp();
  };

  const handleGenerateOtp = async () => {
    if (!canGenerateOtp) return;

    setIsGeneratingOtp(true);
    setError(null);

    try {
      await storeApi.authAPI.generateOtpForLogin({
        mobile: mobileNumber.trim(),
        is_registration: false,
      });
      setOtpSent(true);
    } catch (e) {
      if (isBuyerArchivedError(e)) {
        closeAuthModal?.();
        navigate("/account-not-supported", { replace: true });
        return;
      }
      setError(toErrorText(e, "Unable to send OTP. Please try again."));
    } finally {
      setIsGeneratingOtp(false);
    }
  };

  const handleVerifyAndLogin = async () => {
    if (!canVerify) return;

    setIsVerifying(true);
    setError(null);

    try {
      const body = {
        mobile: mobileNumber.trim(),
        otp_code: otp.trim(),
      };

      const response = await storeApi.authAPI.loginUser(body);

      const root = response || {};
      const inner = root.data || {};
      const user = inner.user || root.user || null;
      const token = inner.token || root.token || null;
      const becameProspect = Boolean(inner?.auth?.became_prospect);

      if (!user) {
        setError("Login failed: Missing user information");
        return;
      }

      if (token) localStorage.setItem("storeAccessToken", token);
      setAuthenticatedUser({ token: token || "", user });
      setCurrentUser(getCurrentUser());

      // Prefer the freshest session user for pre-filling the profile form.
      let prefUser = user;
      try {
        const sessionResponse = await storeApi.sessionAPI.getCurrentSession();
        const sessionUser =
          sessionResponse?.user ||
          sessionResponse?.data?.user ||
          sessionResponse?.data?.data?.user ||
          null;
        if (sessionUser) prefUser = sessionUser;
        if (typeof setDealerReferral === "function")
          setDealerReferral(sessionResponse?.dealer_referral || null);
      } catch (sessionErr) {
        console.warn("Store session verification failed", sessionErr);
      }

      // Only show profile screen when this OTP login transitioned lead->prospect.
      if (becameProspect) {
        const buyerProfileType = String(prefUser?.buyer?.profile_type ?? "").trim();
        if (typeof onRequireRegistration === "function") {
          onRequireRegistration({
            mobile: String(prefUser?.mobile ?? mobileNumber).trim(),
            name: String(prefUser?.name ?? "").trim(),
            companyName: String(prefUser?.buyer_name ?? "").trim(),
            projectType: buyerProfileType || null,
          });
          return;
        }
        navigate("/register", {
          replace: true,
          state: {
            postLoginProfile: true,
            mobile: String(prefUser?.mobile ?? mobileNumber).trim(),
            name: String(prefUser?.name ?? "").trim(),
            companyName: String(prefUser?.buyer_name ?? "").trim(),
            projectType: buyerProfileType || null,
          },
        });
        return;
      }

      // Meta call point: successful login/price unlock completion.
      trackZbRegistrationComplete();

      const returnTarget =
        typeof popAuthModalReturnTarget === "function"
          ? popAuthModalReturnTarget()
          : null;
      if (typeof onClose === "function") onClose();
      if (returnTarget?.pathname) {
        navigate(returnTarget.pathname, {
          replace: true,
          ...(returnTarget.state !== undefined
            ? { state: returnTarget.state }
            : {}),
        });
      } else {
        const user = getCurrentUser();
        if (user && user.customer) navigate("/orders", { replace: true });
        else navigate("/products", { replace: true });
      }
    } catch (e) {
      if (isBuyerArchivedError(e)) {
        closeAuthModal?.();
        navigate("/account-not-supported", { replace: true });
        return;
      }
      setError(toErrorText(e, "Invalid OTP. Please try again."));
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Modal isOpen={isOpen} centered={false} toggle={handleClose} size="md">
      <ModalHeader toggle={handleClose}>Login</ModalHeader>
      <ModalBody>
        <Form>
          <FormGroup noMargin className="mb-3">
            <AuthInput
              disabled={otpSent}
              invalid={
                !otpSent && mobileContinueAttempted && !isMobileValid
              }
              type="text"
              value={mobileNumber}
              placeholder="Mobile Number"
              onChange={(ev) => setMobileNumber(ev.target.value)}
              className="mt-1 unmask"
            />
            {!otpSent ? (
              <StoreWhatsappOtpHint variant="will_be_sent" />
            ) : null}
            {mobileContinueAttempted && !isMobileValid && !otpSent ? (
              <FormFeedback>
                Please enter a valid 10 digit mobile number.
              </FormFeedback>
            ) : null}
            {otpSent ? (
              <div className="d-block mt-1">
                <button
                  type="button"
                  className="btn btn-link p-0 text-decoration-none text-link small"
                  onClick={handleChangeMobile}
                >
                  <i className="fa fa-edit"></i> Change
                </button>
              </div>
            ) : null}
          </FormGroup>

          {otpSent && (
            <FormGroup noMargin className="mb-3">
              <AuthInput
                type="text"
                value={otp}
                placeholder="Enter OTP"
                onChange={(ev) => setOtp(ev.target.value)}
                className="unmask"
              />
              <StoreWhatsappOtpHint
                variant="sent_to"
                mobile={mobileNumber}
                className="d-block mt-2 pb-1"
              />
            </FormGroup>
          )}

          {error ? (
            <Alert color="danger" className="mb-0">
              <span>{error}</span>
            </Alert>
          ) : null}

          <div className="d-grid gap-2">
            {!otpSent ? (
              <AuthButton
                onClick={handleContinue}
                disabled={isGeneratingOtp || otpSent}
                color="primary"
              >
                {isGeneratingOtp ? (
                  <>
                    <Spinner size="sm">Loading...</Spinner> &nbsp;
                    <span>Sending OTP...</span>
                  </>
                ) : (
                  "Continue"
                )}
              </AuthButton>
            ) : (
              <AuthButton
                onClick={handleVerifyAndLogin}
                disabled={!canVerify}
                color="primary"
              >
                {isVerifying ? (
                  <>
                    <Spinner size="sm">Loading...</Spinner> &nbsp;
                    <span>Verifying...</span>
                  </>
                ) : (
                  "Continue"
                )}
              </AuthButton>
            )}
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default StoreLoginWithOtp;
